import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Dropdown, Space, message } from 'antd';
import { DeleteOutlined, DownOutlined, DragOutlined, DownloadOutlined, RedoOutlined } from '@ant-design/icons';
import Utils from '@utils/utils';
import PSAIModal from '@PSAIComponents/PSAIModal';
import PickFromProject from '../PickFromProject';
import useResizeObserver from '@utils/useResizeObserver';
import API from '@api/api';
import { AppContext } from '@utils/AppContext';
import { AIEditContext } from '../../AIEditContext';
import styles from './index.module.scss';

const BOX_PADDING_X = 40;
const BOX_PADDING_Y = 40;

const getTargetRatio = (ratio, imgWidth, imgHeight) => {
    if (ratio === 'origin') {
        if (!imgWidth || !imgHeight) return 1;
        return imgWidth / imgHeight;
    }
    const [width, height] = String(ratio).split(':').map(Number);
    if (!width || !height) return 1;
    return width / height;
};

const parseRatio = (ratio, imgWidth, imgHeight) => {
    return getTargetRatio(ratio, imgWidth, imgHeight);
};

const getBaseCanvasSize = (imgWidth, imgHeight, ratio) => {
    if (!imgWidth || !imgHeight) {
        return { width: 0, height: 0 };
    }

    const targetRatio = getTargetRatio(ratio, imgWidth, imgHeight);
    const imageRatio = imgWidth / imgHeight;

    if (Math.abs(imageRatio - targetRatio) < 0.0001) {
        return { width: imgWidth, height: imgHeight };
    }

    if (imageRatio > targetRatio) {
        return {
            width: imgWidth,
            height: imgWidth / targetRatio,
        };
    }

    return {
        width: imgHeight * targetRatio,
        height: imgHeight,
    };
};

const OutpaintBottomToolbar = ({
    moveMode,
    onToggleMove,
    onReset,
    onRemove,
    onUpload,
    onProjectImageChange,
    downloadDisabled,
    onDownload,
}) => {
    const projectListRef = useRef();
    const [open, setOpen] = useState(false);

    const reuploadItems = [
        { label: '本地上传', key: 'local' },
        { label: '从项目选图', key: 'project' },
    ];

    const handleReuploadClick = (event) => {
        const status = Utils.checkLoginStatus(() => handleReuploadClick(event));
        if (!status) return;

        if (event.key === 'local') {
            Utils.openFile2(false).then(onUpload).catch(() => { });
            return;
        }

        setOpen(true);
    };

    const handleProjectOk = async () => {
        const url = await projectListRef.current?.onSubmit?.();
        if (!url) return false;
        setOpen(false);
        onProjectImageChange?.(url);
    };

    const handleDoubleClick = (url) => {
        if (!url) return;
        setOpen(false);
        onProjectImageChange?.(url);
    };

    return (
        <div className={styles.ToolbarRow}>
            <div className={styles.ToolbarGroup}>
                <button type="button" className={styles.ToolbarItem} onClick={onReset}>
                    <RedoOutlined />
                    <span>重置</span>
                </button>
                <button
                    type="button"
                    className={`${styles.ToolbarItem} ${moveMode ? styles.ToolbarItemActive : ''}`}
                    onClick={onToggleMove}
                >
                    <DragOutlined />
                    <span>移动</span>
                </button>
            </div>
            <span className={styles.Divider}></span>
            <div className={styles.ToolbarGroup}>
                <button type="button" className={styles.ToolbarItem} onClick={onRemove}>
                    <DeleteOutlined />
                    <span>删除</span>
                </button>
            </div>
            <span className={styles.Divider}></span>
            <div className={styles.ActionGroup}>
                <Dropdown trigger={['click']} menu={{ items: reuploadItems, onClick: handleReuploadClick }} overlayClassName={styles.Dropdown}>
                    <Button className={styles.PrimaryButton}>
                        <Space>
                            重新上传
                            <DownOutlined className={styles.DropIcon} />
                        </Space>
                    </Button>
                </Dropdown>
                <Button className={styles.PrimaryButton} type="primary" disabled={downloadDisabled} onClick={onDownload}>
                    <DownloadOutlined />
                    <span>下载图片</span>
                </Button>
            </div>
            <PSAIModal
                open={open}
                maxWidth={980}
                title="选择图片"
                onOk={handleProjectOk}
                onCancel={() => setOpen(false)}
            >
                <PickFromProject
                    ref={projectListRef}
                    onDoubleClick={handleDoubleClick}
                    showAllType
                />
            </PSAIModal>
        </div>
    );
};

const Outpaint = (props) => {
    const { AIEditRecentTaskId } = useContext(AppContext);
    const { AIEditLoading } = useContext(AIEditContext);
    const [imageMeta, setImageMeta] = useState({ width: 0, height: 0 });
    const [offset, setOffset] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const dragRef = useRef(null);
    const [stageSize, setStageSize] = useState({ width: 0, height: 0 });
    const stageRef = useResizeObserver((entries) => {
        const entry = entries?.[0];
        if (!entry) return;
        setStageSize({
            width: entry.contentRect.width,
            height: entry.contentRect.height,
        });
    });

    useEffect(() => {
        if (!props.url) return;
        let active = true;
        Utils.getImageDimensions(props.url).then(({ width, height }) => {
            if (!active) return;
            setImageMeta({ width, height });
        });
        return () => {
            active = false;
        };
    }, [props.url]);

    const previewRatio = useMemo(
        () => parseRatio(props.ratio, imageMeta.width, imageMeta.height),
        [props.ratio, imageMeta.width, imageMeta.height]
    );

    const previewBox = useMemo(() => {
        const availableWidth = Math.max(stageSize.width - BOX_PADDING_X * 2, 200);
        const availableHeight = Math.max(stageSize.height - BOX_PADDING_Y * 2, 200);
        let width = availableWidth;
        let height = width / previewRatio;

        if (height > availableHeight) {
            height = availableHeight;
            width = height * previewRatio;
        }

        return { width, height };
    }, [previewRatio, stageSize.height, stageSize.width]);

    const baseImageSize = useMemo(() => {
        if (!imageMeta.width || !imageMeta.height || !previewBox.width || !previewBox.height) {
            return { width: 0, height: 0 };
        }
        const scale = Math.min(previewBox.width / imageMeta.width, previewBox.height / imageMeta.height);
        return {
            width: imageMeta.width * scale,
            height: imageMeta.height * scale,
        };
    }, [imageMeta.height, imageMeta.width, previewBox.height, previewBox.width]);

    const shrinkScale = useMemo(() => 100 / (props.multiple || 100), [props.multiple]);

    const clampedOffset = useCallback((nextOffset) => {
        const scaledWidth = baseImageSize.width * shrinkScale;
        const scaledHeight = baseImageSize.height * shrinkScale;
        const maxX = Math.max((previewBox.width - scaledWidth) / 2, 0);
        const maxY = Math.max((previewBox.height - scaledHeight) / 2, 0);

        return {
            x: Math.min(Math.max(nextOffset.x, -maxX), maxX),
            y: Math.min(Math.max(nextOffset.y, -maxY), maxY),
        };
    }, [baseImageSize.height, baseImageSize.width, previewBox.height, previewBox.width, shrinkScale]);

    useEffect(() => {
        setOffset((prev) => clampedOffset(prev));
    }, [clampedOffset, props.multiple, props.ratio]);

    useEffect(() => {
        setOffset({ x: 0, y: 0 });
    }, [props.url, props.resetSeed]);

    useEffect(() => {
        if (!props.onLayoutChange) return;

        if (!props.url || !imageMeta.width || !imageMeta.height || !baseImageSize.width || !baseImageSize.height) {
            props.onLayoutChange(null);
            return;
        }

        const baseCanvasSize = getBaseCanvasSize(imageMeta.width, imageMeta.height, props.ratio);
        const upscaleFactor = (props.multiple || 100) / 100;
        const canvasWidth = Math.round(baseCanvasSize.width * upscaleFactor);
        const canvasHeight = Math.round(baseCanvasSize.height * upscaleFactor);
        const previewImageWidth = baseImageSize.width * shrinkScale;
        const previewImageHeight = baseImageSize.height * shrinkScale;
        const scaleToOutputX = previewImageWidth ? imageMeta.width / previewImageWidth : 1;
        const scaleToOutputY = previewImageHeight ? imageMeta.height / previewImageHeight : 1;
        const outputOffsetX = offset.x * scaleToOutputX;
        const outputOffsetY = offset.y * scaleToOutputY;
        const x1 = Math.round(canvasWidth / 2 - imageMeta.width / 2 + outputOffsetX);
        const y1 = Math.round(canvasHeight / 2 - imageMeta.height / 2 + outputOffsetY);
        props.onLayoutChange({
            canvasWidth,
            canvasHeight,
            coordinate: `${canvasWidth},${canvasHeight},${x1},${y1},${imageMeta.width},${imageMeta.height}`,
            imageWidth: imageMeta.width,
            imageHeight: imageMeta.height,
        });
    }, [baseImageSize.height, baseImageSize.width, imageMeta.height, imageMeta.width, offset.x, offset.y, props.multiple, props.onLayoutChange, props.ratio, props.url, shrinkScale]);

    useEffect(() => {
        const handleMouseMove = (event) => {
            if (!dragRef.current) return;
            const deltaX = event.clientX - dragRef.current.startX;
            const deltaY = event.clientY - dragRef.current.startY;
            setOffset(clampedOffset({
                x: dragRef.current.originX + deltaX,
                y: dragRef.current.originY + deltaY,
            }));
        };

        const handleMouseUp = () => {
            dragRef.current = null;
            setIsDragging(false);
        };

        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [clampedOffset]);

    const handlePointerDown = (event) => {
        if (!props.moveMode) return;
        event.preventDefault();
        setIsDragging(true);
        dragRef.current = {
            startX: event.clientX,
            startY: event.clientY,
            originX: offset.x,
            originY: offset.y,
        };
    };

    const commonDownloadFunc = async () => {
        const status = Utils.checkLoginStatus(() => commonDownloadFunc());
        if (!status) return;

        try {
            const res = await API.commonDownload({ taskId: props.taskId || AIEditRecentTaskId });
            if (res?.code != 0) throw new Error(res?.message);
            Utils.downUrl(res.data, 'aiEdit');
            message.success('下载成功');
        } catch (err) {
            message.error(err.message || '下载失败');
        }
    };

    useEffect(() => {
        if (!props.onBottomConfigChange) return;

        if (!props.url) {
            props.onBottomConfigChange(null);
            return;
        }

        props.onBottomConfigChange({
            customContent: (
                <OutpaintBottomToolbar
                    moveMode={props.moveMode}
                    onToggleMove={props.onToggleMoveMode}
                    onReset={props.onReset}
                    onRemove={props.handleCleanClick}
                    onUpload={props.uploadCb}
                    onProjectImageChange={props.onProjectReupload}
                    downloadDisabled={AIEditLoading || !(props.taskId || AIEditRecentTaskId)}
                    onDownload={commonDownloadFunc}
                />
            )
        });

        return () => {
            props.onBottomConfigChange(null);
        };
    }, [AIEditLoading, AIEditRecentTaskId, props.handleCleanClick, props.moveMode, props.onBottomConfigChange, props.onProjectReupload, props.onReset, props.onToggleMoveMode, props.taskId, props.uploadCb, props.url]);

    const content = !props.url ? props.defaultPage : (
        <div className={styles.OutpaintCanvas}>
            <div className={styles.PreviewStage} ref={stageRef}>
                <div
                    className={styles.PreviewBox}
                    style={{
                        width: `${previewBox.width}px`,
                        height: `${previewBox.height}px`,
                    }}
                >
                    {isDragging && Math.abs(offset.x) <= 3 ? (
                        <div className={styles.CenterGuideVertical} />
                    ) : null}
                    {isDragging && Math.abs(offset.y) <= 3 ? (
                        <div className={styles.CenterGuideHorizontal} />
                    ) : null}
                    <img
                        src={props.url}
                        alt=""
                        draggable={false}
                        onMouseDown={handlePointerDown}
                        className={`${styles.PreviewImage} ${props.moveMode ? styles.PreviewImageMove : ''}`}
                        style={{
                            width: `${baseImageSize.width}px`,
                            height: `${baseImageSize.height}px`,
                            transform: `translate(calc(-50% + ${offset.x}px), calc(-50% + ${offset.y}px)) scale(${shrinkScale})`,
                        }}
                    />
                </div>
            </div>
        </div>
    );

    return (
        <div className={styles.Outpaint}>
            <div className={styles.OutpaintBody}>
                {content}
            </div>
        </div>
    );
};

export default Outpaint;
